import type { FileInputProps } from './file-input';
import type { FileUploadProgressProps } from './file-upload-progress';

import React, { useState } from 'react';

import { FileInput } from './file-input';
import { FileUploadProgress } from './file-upload-progress';

export interface FileData {
  src: string;
  name: string;
  size: number;
}

export type FileUploaderProps = Omit<FileInputProps, 'onChange'> & {
  progress?: FileUploadProgressProps['progress'];
  errorMessage?: FileUploadProgressProps['errorMessage'];
  testIdPrefix?: string;
  onChange?: (file: File) => void;
  onClose?: FileUploadProgressProps['onClose'];
  onFileRead?: (fileData: FileData) => void;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
};

/**
 * @param {number} [maximumFileSize] The maximum size of the file in bytes.
 * @default undefined
 * @param {string} [acceptedFormats] A comma-separated list of accepted file formats (e.g. ".jpg, .jpeg, .png").
 * @default undefined
 * @param {boolean} [disabled] Whether the  upload is disabled or not.
 * @default false
 * @param {ReactNode} [icon] Optional component's icon.
 * @default undefined
 * @param {string} [heading] Optional component's heading.
 * @default undefined
 * @param {string} [info] Optional component's info.
 * @default undefined
 * @param {string[]} [additionalUploadInformation] Optional additional upload information (e.g. file restrictions).
 * If the array contains more than one element, it will be rendered as a list. Otherwise it will be rendered as plain text.
 * @default undefined
 * @param {number} [progress] File upload progress.
 * @default undefined
 * @param {string} [errorMessage] Error message (usually one coming from the server).
 * @default undefined
 * @param {string} [testIdPrefix] Optional data-testid prefix.
 * @default ''
 * @param {Function} [onChange] A callback that is called when file is chosen.
 * @default undefined
 * @param {Function} [onError] A callback that is called when file does not meet the requirements.
 * @default []
 * @param {Function} [onClose] A callback that is called when the upload progress is closed.
 * @default undefined
 * @param {Function} [onFileRead] A callback that is called when the file is read using "FileReader.readAsDataURL()"
 * @default undefined
 * @param {Function} [onLoadStart] A callback that is called when the file starts loading.
 * @default undefined
 * @param {Function} [onLoadEnd] A callback that is called when the file ends loading.
 * @default undefined
 */
export function FileUploader({
  maximumFileSize,
  acceptedFormats,
  disabled,
  icon,
  heading,
  info,
  additionalUploadInformation,
  progress,
  errorMessage,
  testIdPrefix = '',
  children,
  onChange,
  onError,
  onClose,
  onFileRead,
  onLoadStart,
  onLoadEnd,
  ...props
}: FileUploaderProps) {
  const [fileData, setFileData] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [fileSize, setFileSize] = useState<number>();

  const progressVisible = ![fileData, fileName, fileSize].includes(undefined);

  const handleChange = (validFiles: File[]) => {
    const file = validFiles[0];

    if (!file) return;

    onChange?.(file);
    onLoadStart?.();

    const reader = new FileReader();

    reader.onload = () => {
      const fileSrc = reader.result as string;

      setFileData(fileSrc);
      setFileName(file.name);
      setFileSize(file.size);

      onFileRead?.({ src: fileSrc, name: file.name, size: file.size });
      onLoadEnd?.();
    };

    reader.readAsDataURL(file);
  };

  const handleUploadProgressClose = () => {
    setFileData(undefined);
    setFileName(undefined);
    setFileSize(undefined);
    onClose?.();
  };

  if (progressVisible)
    return (
      <FileUploadProgress
        fileData={fileData}
        fileName={fileName}
        fileSize={fileSize}
        progress={progress}
        errorMessage={errorMessage}
        testIdPrefix={testIdPrefix}
        onClose={handleUploadProgressClose}
        {...props}
      />
    );

  return (
    <FileInput
      maximumFileSize={maximumFileSize}
      acceptedFormats={acceptedFormats}
      disabled={disabled}
      icon={icon}
      heading={heading}
      info={info}
      additionalUploadInformation={additionalUploadInformation}
      testIdPrefix={testIdPrefix}
      onChange={handleChange}
      onError={onError}
      children={children}
      multiple={true}
      {...props}
    />
  );
}
